import { InvalidScientificEntryError } from '../errors/InvalidScientificEntryError';

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * A single feature covers both کانی‌شناسی (mineralogy) and سنگ‌شناسی
 * (petrology) rather than duplicating the entity, since the two only differ
 * by this field — everything else (physical properties, images, references,
 * draft/publish workflow) is identical editorial structure.
 */
export type ScientificEntryCategory = 'mineral' | 'rock';
const VALID_CATEGORIES: ScientificEntryCategory[] = ['mineral', 'rock'];

export type ScientificEntryStatus = 'draft' | 'published';
const VALID_STATUSES: ScientificEntryStatus[] = ['draft', 'published'];

export interface PhysicalProperties {
  hardness: string | null;
  color: string | null;
  luster: string | null;
  streak: string | null;
  crystalSystem: string | null;
  cleavage: string | null;
  fracture: string | null;
  specificGravity: string | null;
}

export interface EntryImage {
  url: string;
  caption: string;
}

export interface EntryReference {
  title: string;
  url: string | null;
  author: string | null;
}

export interface ScientificEntryInput {
  id: string;
  slug: string;
  title: string;
  category: ScientificEntryCategory;
  classification: string;
  chemicalFormula: string | null;
  physicalProperties: PhysicalProperties;
  description: string;
  images: EntryImage[];
  occurrenceLocations: string[];
  references: EntryReference[];
  status: ScientificEntryStatus;
  createdAt: string;
  updatedAt: string;
}

/**
 * Entity per the pattern in features/minerals (Mineral.entity.ts).
 *
 * Real business rules:
 *  - A `draft` may be incomplete — that is the entire point of drafts, so an
 *    author (Daruosh) can start an entry and come back to it later.
 *  - A `published` entry must actually be usable content: it needs a
 *    description and at least one image. Publishing an empty page to the
 *    public site is a content-entry mistake, not a valid state — same
 *    principle as Mineral's "at least one image" rule, just conditioned on
 *    status instead of being unconditional.
 */
export class ScientificEntry {
  private constructor(public readonly props: ScientificEntryInput) {
    Object.freeze(this.props.physicalProperties);
    Object.freeze(this.props);
    Object.freeze(this);
  }

  public static create(input: ScientificEntryInput): ScientificEntry {
    const title = input.title.trim();
    const slug = input.slug.trim();
    const classification = input.classification.trim();
    const description = input.description.trim();

    if (title.length < 2 || title.length > 150) {
      throw new InvalidScientificEntryError('title must be between 2 and 150 characters.');
    }
    if (!SLUG_PATTERN.test(slug)) {
      throw new InvalidScientificEntryError(`slug "${slug}" must be lowercase kebab-case.`);
    }
    if (!VALID_CATEGORIES.includes(input.category)) {
      throw new InvalidScientificEntryError(`category must be one of: ${VALID_CATEGORIES.join(', ')}.`);
    }
    if (!VALID_STATUSES.includes(input.status)) {
      throw new InvalidScientificEntryError(`status must be one of: ${VALID_STATUSES.join(', ')}.`);
    }
    if (classification.length === 0) {
      throw new InvalidScientificEntryError('classification must not be empty.');
    }
    if (input.images.some((image) => image.url.trim().length === 0)) {
      throw new InvalidScientificEntryError('images must not contain an empty url.');
    }
    if (input.references.some((reference) => reference.title.trim().length === 0)) {
      throw new InvalidScientificEntryError('references must not contain an empty title.');
    }
    if (input.physicalProperties.hardness) {
      const hardness = Number(input.physicalProperties.hardness);
      if (!Number.isNaN(hardness) && (hardness < 1 || hardness > 10)) {
        throw new InvalidScientificEntryError('hardness, when numeric, must be between 1 and 10 (Mohs scale).');
      }
    }

    if (input.status === 'published') {
      if (description.length === 0) {
        throw new InvalidScientificEntryError('a published entry must have a description.');
      }
      if (input.images.length === 0) {
        throw new InvalidScientificEntryError('a published entry must have at least one image.');
      }
    }

    return new ScientificEntry({ ...input, title, slug, classification, description });
  }
}
